import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Clock, ChevronDown, ChevronRight, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
// BestDriveChatbot is mounted globally in App.tsx

const MarketingHeader: React.FC<{ showBookOnline?: boolean }> = ({ showBookOnline = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>('lessons'); // Auto-expand Lessons by default
  const [desktopDropdown, setDesktopDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Apply premium dark marketing theme globally while a marketing page is mounted
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('marketing-theme');
    body.classList.add('marketing-theme');
    return () => {
      root.classList.remove('marketing-theme');
      body.classList.remove('marketing-theme');
    };
  }, []);

  // Reset to default expanded section when menu opens
  useEffect(() => {
    if (mobileMenuOpen) {
      setOpenDropdown('lessons'); // Auto-expand Lessons when menu opens
    }
  }, [mobileMenuOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdown('lessons'); // Reset to default
  };

  // Accordion behavior - only one section open at a time
  const toggleDropdown = (name: string) => {
    // If clicking the same one, close it; otherwise open the new one (closing the previous)
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const handleDesktopMouseEnter = (name: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setDesktopDropdown(name);
  };

  const handleDesktopMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDesktopDropdown(null);
    }, 150);
  };

  // Navigation structure
  const navigationItems = {
    startHere: {
      label: 'Start Here',
      items: [
        { label: 'New Drivers', href: '/new-driver-essentials' },
        { label: 'Nervous Drivers', href: '/nervous-drivers' },
        { label: 'Never Driven Before?', href: '/#booking' },
      ],
    },
    bookLessons: {
      label: 'Book an Assessment',
      href: '/book-now',
    },
    lessons: {
      label: 'Courses',
      items: [
        { label: 'Intensive Course Pricing', href: '/prices' },
        { label: 'Areas I Cover', href: '/areas-covered' },
      ],
    },
    testPrep: {
      label: 'Test Preparation',
      items: [
        { label: 'Before Your Test', href: '/before-your-test' },
        { label: 'Mock Tests', href: '/mock-tests' },
        { label: 'Common Test Routes', href: '/before-your-test#test-centres' },
      ],
    },
    guides: {
      label: 'Guides & Tips',
      items: [
        { label: 'Learning to Drive', href: '/blog' },
        { label: 'Driving Confidence', href: '/nervous-drivers' },
        { label: 'Test Advice', href: '/before-your-test' },
      ],
    },
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-violet via-rose to-sky">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 max-w-7xl mx-auto">
          <Link to="/" className={`${showBookOnline ? 'text-lg sm:text-3xl' : 'text-2xl sm:text-3xl'} font-black text-white tracking-tight shrink-0`}>
            BEST-DRIVE
          </Link>
          
          {/* Opening Hours - Center */}
          <div className={`${showBookOnline ? 'hidden lg:flex' : 'hidden sm:flex'} items-center gap-1.5 text-white/90`}>
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">7AM - 9PM 7 DAYS A WEEK</span>
          </div>
          
          <div className={`flex items-center ${showBookOnline ? 'gap-2' : 'gap-1'} sm:gap-4`}>
            <Link 
              to="/auth"
              className="hidden sm:block text-white text-base sm:text-lg font-medium hover:opacity-80 transition-opacity"
            >
              Login
            </Link>
            
            {/* Mobile Login Icon */}
            {!showBookOnline && (
            <Link
              to="/auth"
              className="sm:hidden text-white p-2 hover:opacity-80 transition-opacity"
              aria-label="Login"
            >
              <LogIn className="h-5 w-5" />
            </Link>
            )}
            
            {showBookOnline && (
              <Button asChild size="sm" className="btn-premium h-11 px-3 sm:px-4 rounded-full text-xs sm:text-sm">
                <Link to="/student-register">Book Online</Link>
              </Button>
            )}

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2 hover:opacity-80 transition-opacity"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Premium Dark Slide-out */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col animate-fade-in"
          style={{ top: 0, left: 0, width: '100%', height: '100vh', touchAction: 'none' }}
          aria-modal="true"
          role="dialog"
        >
          {/* Header bar */}
          <div className="flex-shrink-0 h-16 bg-gradient-to-r from-violet via-rose to-sky flex items-center justify-between px-5 shadow-lg">
            <Link to="/" className="text-2xl font-black text-white tracking-tight" onClick={closeMenu}>
              BEST-DRIVE
            </Link>
            <button
              onClick={closeMenu}
              className="text-white p-2.5 rounded-full hover:bg-white/15 active:scale-95 transition-all"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" strokeWidth={2.25} />
            </button>
          </div>

          {/* Menu Body - premium dark navy */}
          <div
            className="flex-1 overflow-y-auto relative"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              background: 'linear-gradient(180deg, #0b1024 0%, #0f1535 55%, #0a0f28 100%)',
            }}
          >
            {/* Subtle radial glow overlay */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(80% 50% at 50% 0%, rgba(139,92,246,0.18) 0%, rgba(139,92,246,0) 60%), radial-gradient(60% 40% at 100% 100%, rgba(56,189,248,0.12) 0%, rgba(56,189,248,0) 70%)',
              }}
            />

            <nav className="relative px-6 pt-6 pb-40">
              <div className="mb-4 pb-3 border-b border-white/15">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-300/80">Navigation</p>
                <h2 className="mt-1 text-2xl font-black tracking-tight text-white">Menu</h2>
              </div>
              <div className="space-y-1">
                {/* Intensive Driving Courses */}
                <Link
                  to="/intensive-driving-courses"
                  onClick={closeMenu}
                  className="flex items-center justify-between gap-2 w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight border-b border-white/10 active:bg-white/5 transition-colors"
                >
                  <span>Intensive Driving Courses</span>
                  <span className="text-[10px] font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-md">Auto & Manual</span>
                </Link>

                {/* Why Best-Drive */}
                <Link
                  to="/why-best-drive"
                  onClick={closeMenu}
                  className="block w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight border-b border-white/10 active:bg-white/5 transition-colors"
                >
                  Why Best-Drive
                </Link>

                {/* Areas I Cover */}
                <Link
                  to="/areas-covered"
                  onClick={closeMenu}
                  className="block w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight border-b border-white/10 active:bg-white/5 transition-colors"
                >
                  Areas I Cover
                </Link>

                {/* Your Driving Journey */}
                <Link
                  to="/your-driving-journey"
                  onClick={closeMenu}
                  className="block w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight border-b border-white/10 active:bg-white/5 transition-colors"
                >
                  Your Driving Journey
                </Link>



                {/* Intensive Courses by Location */}
                <div className="border-b border-white/10">
                  <button
                    onClick={() => toggleDropdown('locations')}
                    className="flex items-center justify-between w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight active:opacity-80"
                    aria-expanded={openDropdown === 'locations'}
                  >
                    <span>Intensive Courses by Location</span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-300 ${
                        openDropdown === 'locations' ? 'rotate-180 text-fuchsia-400' : 'text-white/60'
                      }`}
                      strokeWidth={2.25}
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      openDropdown === 'locations' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-4 pl-3 space-y-1">
                        <Link to="/intensive-driving-courses-bath" onClick={closeMenu} className="block py-3 px-3 min-h-[48px] text-white/85 hover:text-white text-base font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors">
                          Bath
                        </Link>
                        <Link to="/intensive-driving-courses-chippenham" onClick={closeMenu} className="block py-3 px-3 min-h-[48px] text-white/85 hover:text-white text-base font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors">
                          Chippenham
                        </Link>
                        <Link to="/intensive-driving-courses-kingswood" onClick={closeMenu} className="block py-3 px-3 min-h-[48px] text-white/85 hover:text-white text-base font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors">
                          Kingswood
                        </Link>
                        <Link to="/intensive-driving-courses-bristol" onClick={closeMenu} className="block py-3 px-3 min-h-[48px] text-white/85 hover:text-white text-base font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors">
                          Bristol
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Direct links */}
                {[
                  { label: 'About Ian', href: '/about-ian' },
                  { label: 'Reviews', href: '/reviews' },
                  { label: 'Guides & Tips', href: '/blog' },
                  { label: 'Contact', href: '/contact' },
                  { label: 'Insurance and Tax, MOT', href: '/new-driver-essentials' },
                  ...(showBookOnline ? [{ label: 'Login', href: '/auth' }] : []),
                ].map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={closeMenu}
                    className="block w-full py-5 min-h-[56px] text-white font-bold text-lg tracking-tight border-b border-white/10 active:bg-white/5 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}

                {/* Highlighted: Become an Instructor */}
                <Link
                  to="/become-a-driving-instructor"
                  onClick={closeMenu}
                  className="mt-4 flex items-center justify-between gap-2 w-full py-4 px-4 rounded-xl text-white font-extrabold text-base tracking-tight bg-gradient-to-r from-amber-500 via-fuchsia-600 to-violet-600 shadow-[0_8px_30px_-8px_rgba(217,70,239,0.6)] ring-1 ring-white/20 active:scale-[0.98] hover:brightness-110 transition-all"
                >
                  <span className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-[10px] uppercase tracking-wider font-bold">New</span>
                    Become a Driving Instructor
                  </span>
                  <span aria-hidden="true">→</span>
                </Link>



              </div>

            </nav>
          </div>

        </div>
      )}
    </>
  );
};

export default MarketingHeader;
